/**
 * Typed API client generated from the route contract map in
 * `@algoboo/schema/api`. Every response is validated fail-closed with the
 * route's zod schema: data that does not match is an error, never rendered.
 */
import {
  API_BASE,
  ApiError as ApiErrorBody,
  AgentAvatar,
  type EvidenceObject,
  buildPath,
  routes,
  type RouteName,
  type RouteParamsInput,
  type RouteQueryInput,
  type RouteBodyInput,
  type RouteResponse,
} from '@algoboo/schema/api'
import type { z } from 'zod'
import { API_TIMEOUT_MS } from './config'
import { ApiError, type ApiErrorCode } from './errors'

type Maybe<K extends string, T> = [T] extends [undefined] ? { [P in K]?: never } : { [P in K]: T }
type MaybeOptional<K extends string, T> = [T] extends [undefined] ? { [P in K]?: never } : { [P in K]?: T }

/**
 * What a caller passes for route N. `params` and `body` follow the route
 * schema; `query` is always optional (every field is validated server-side).
 */
export type ApiRequest<N extends RouteName> = Maybe<'params', RouteParamsInput<N>> &
  MaybeOptional<'query', RouteQueryInput<N>> &
  Maybe<'body', RouteBodyInput<N>> & {
    signal?: AbortSignal
    idempotencyKey?: string
  }

export interface RequestOptions {
  fetch?: typeof fetch
  timeoutMs?: number
  /** Retries for GET on network errors, 429 and 502-504. Default 2. */
  retries?: number
  sleep?: (ms: number) => Promise<void>
}

const RETRYABLE_STATUS = new Set([429, 502, 503, 504])
const MAX_RETRY_AFTER_MS = 10_000

function toQueryString(query: unknown): string {
  if (!query || typeof query !== 'object') return ''
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(query as Record<string, unknown>)) {
    if (v === undefined || v === null) continue
    if (Array.isArray(v)) {
      if (v.length === 0) continue
      sp.set(k, v.map(String).join(','))
    } else sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/** Relative URL of route N (the Vite dev proxy forwards /api). */
export function buildUrl<N extends RouteName>(name: N, req: { params?: unknown; query?: unknown } = {}): string {
  return `${API_BASE}${buildPath(name, (req.params ?? {}) as Record<string, string>)}${toQueryString(req.query)}`
}

/** Parse `Retry-After` (delta seconds or HTTP date) into ms, capped. */
export function parseRetryAfter(value: string | null, now = Date.now()): number | undefined {
  if (!value) return undefined
  const trimmed = value.trim()
  let ms: number
  if (/^\d+(\.\d+)?$/.test(trimmed)) ms = Number(trimmed) * 1000
  else {
    const t = Date.parse(trimmed)
    if (Number.isNaN(t)) return undefined
    ms = t - now
  }
  return Math.max(0, Math.min(MAX_RETRY_AFTER_MS, ms))
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

function backoff(attempt: number): number {
  return 400 * 2 ** attempt + Math.floor(Math.random() * 150)
}

function newIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

async function readJson(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined
  const text = await res.text()
  if (!text) return undefined
  try {
    return JSON.parse(text) as unknown
  } catch {
    return Symbol.for('algoboo.unparseable')
  }
}

const loggedInvalid = new Set<string>()

interface RawCall {
  method: string
  url: string
  routeName: string
  body?: unknown
  idempotencyKey?: string
  signal?: AbortSignal | undefined
  schema: z.ZodType
}

async function call(raw: RawCall, opts: RequestOptions): Promise<unknown> {
  const fetchImpl = opts.fetch ?? globalThis.fetch
  const sleep = opts.sleep ?? defaultSleep
  const retries = raw.method === 'GET' ? (opts.retries ?? 2) : 0
  const timeoutMs = opts.timeoutMs ?? API_TIMEOUT_MS
  const headers: Record<string, string> = { Accept: 'application/json' }
  let bodyText: string | undefined
  if (raw.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    bodyText = JSON.stringify(raw.body)
  }
  if (raw.method !== 'GET') headers['Idempotency-Key'] = raw.idempotencyKey ?? newIdempotencyKey()

  for (let attempt = 0; ; attempt++) {
    if (raw.signal?.aborted) throw new ApiError({ code: 'ABORTED', status: 0, route: raw.routeName })
    const controller = new AbortController()
    let timedOut = false
    const timer = setTimeout(() => {
      timedOut = true
      controller.abort()
    }, timeoutMs)
    const onAbort = () => controller.abort()
    raw.signal?.addEventListener('abort', onAbort)

    let res: Response
    try {
      const init: RequestInit = { method: raw.method, headers, credentials: 'include', signal: controller.signal }
      if (bodyText !== undefined) init.body = bodyText
      res = await fetchImpl(raw.url, init)
    } catch (e) {
      clearTimeout(timer)
      raw.signal?.removeEventListener('abort', onAbort)
      if (timedOut) throw new ApiError({ code: 'TIMEOUT', status: 0, route: raw.routeName })
      if (raw.signal?.aborted) throw new ApiError({ code: 'ABORTED', status: 0, route: raw.routeName })
      if (attempt < retries) {
        await sleep(backoff(attempt))
        continue
      }
      throw new ApiError({ code: 'NETWORK', status: 0, route: raw.routeName, details: String(e) })
    }

    let payload: unknown
    try {
      payload = await readJson(res)
    } catch (e) {
      if (timedOut) throw new ApiError({ code: 'TIMEOUT', status: 0, route: raw.routeName })
      throw new ApiError({ code: 'NETWORK', status: res.status, route: raw.routeName, details: String(e) })
    } finally {
      clearTimeout(timer)
      raw.signal?.removeEventListener('abort', onAbort)
    }

    if (!res.ok) {
      const retryAfterMs = parseRetryAfter(res.headers.get('Retry-After'))
      if (RETRYABLE_STATUS.has(res.status) && attempt < retries) {
        await sleep(retryAfterMs ?? backoff(attempt))
        continue
      }
      const parsed = ApiErrorBody.safeParse(payload)
      if (parsed.success) {
        const init: ConstructorParameters<typeof ApiError>[0] = {
          code: parsed.data.error.code,
          status: res.status,
          message: parsed.data.error.message,
          details: parsed.data.error.details,
          route: raw.routeName,
        }
        if (retryAfterMs !== undefined) init.retryAfterMs = retryAfterMs
        throw new ApiError(init)
      }
      const init: ConstructorParameters<typeof ApiError>[0] = { code: 'HTTP' satisfies ApiErrorCode, status: res.status, route: raw.routeName, details: payload }
      if (retryAfterMs !== undefined) init.retryAfterMs = retryAfterMs
      throw new ApiError(init)
    }

    const result = raw.schema.safeParse(payload)
    if (!result.success) {
      if (!loggedInvalid.has(raw.routeName)) {
        loggedInvalid.add(raw.routeName)
        console.error(`[api] ${raw.routeName}: response failed validation`, result.error.issues)
      }
      throw new ApiError({ code: 'INVALID_RESPONSE', status: res.status, route: raw.routeName, details: result.error.issues })
    }
    recordAvatars(result.data)
    return result.data
  }
}

/** Call route N. Throws `ApiError` on every failure. */
export async function request<N extends RouteName>(name: N, req: ApiRequest<N>, opts: RequestOptions = {}): Promise<RouteResponse<N>> {
  const route = routes[name]
  const r = req as { params?: unknown; query?: unknown; body?: unknown; signal?: AbortSignal; idempotencyKey?: string }
  const raw: RawCall = {
    method: route.method,
    url: buildUrl(name, r),
    routeName: name,
    signal: r.signal,
    schema: route.response,
  }
  if (r.body !== undefined) raw.body = r.body
  if (r.idempotencyKey !== undefined) raw.idempotencyKey = r.idempotencyKey
  return (await call(raw, opts)) as RouteResponse<N>
}

/** GET /api/v1/evidence/:id (public): one evidence object; the drawer recomputes its hash. */
export async function getEvidence(id: string, opts: RequestOptions & { signal?: AbortSignal } = {}): Promise<EvidenceObject> {
  return request('getEvidence', { params: { id }, ...(opts.signal ? { signal: opts.signal } : {}) }, opts)
}

// Avatar registry ------------------------------------------------------------------
// AgentSummary carries `avatar` now; this registry is only the fallback for
// rows whose avatar is null (e.g. an older API), filled from every configured
// avatar we have seen.

const avatars = new Map<string, AgentAvatar>()
const avatarListeners = new Set<() => void>()
let avatarVersion = 0

function recordAvatars(data: unknown, depth = 0): void {
  let changed = false
  const walk = (v: unknown, d: number) => {
    if (d > 6 || v === null || typeof v !== 'object') return
    if (Array.isArray(v)) {
      for (const x of v) walk(x, d + 1)
      return
    }
    const o = v as Record<string, unknown>
    if (typeof o['handle'] === 'string' && o['avatar'] && typeof o['avatar'] === 'object') {
      const parsed = AgentAvatar.safeParse(o['avatar'])
      if (parsed.success) {
        const prev = avatars.get(o['handle'])
        if (!prev || JSON.stringify(prev) !== JSON.stringify(parsed.data)) {
          avatars.set(o['handle'], parsed.data)
          changed = true
        }
      }
    }
    for (const x of Object.values(o)) if (x && typeof x === 'object') walk(x, d + 1)
  }
  walk(data, depth)
  if (changed) {
    avatarVersion++
    for (const fn of avatarListeners) fn()
  }
}

export function knownAvatar(handle: string): AgentAvatar | undefined {
  return avatars.get(handle)
}

export function avatarsVersion(): number {
  return avatarVersion
}

export function subscribeAvatars(fn: () => void): () => void {
  avatarListeners.add(fn)
  return () => {
    avatarListeners.delete(fn)
  }
}
